import { ref } from '@vue/composition-api'

import { AuthenticationService } from '@services'

/**
 * Shared sign-in routing for the login page: does this email belong to a
 * migrated user, and is this their first time signing in via SSO.
 *
 * Nothing here grants access. Every decision is a UI routing hint; the backend
 * gate is the OIDC backend's filter_users_by_claims (per user).
 *
 * @param {string} [next] - Where to return to after a successful login (e.g.
 *   Django admin sent the user here with ?next=/backend/411/). Forwarded onto
 *   sso_login_url so the backend's own oidc_login_next handling takes over;
 *   left out entirely for an ordinary login (lands on the dashboard as usual).
 */
export const useSsoLogin = (next) => {
  const withNext = (url) => {
    if (!next) return url
    return `${url}${url.includes('?') ? '&' : '?'}next=${encodeURIComponent(next)}`
  }

  const checkingSso = ref(false)
  const showFirstTimePrompt = ref(false)
  const showSetPassword = ref(false)
  const nativeAuthEnabled = ref(false)
  const ssoLoginUrl = ref('')

  // Pre-fetches a destination for the SSO button/link before anyone has typed
  // an email, so it isn't left blank until the per-email discover call runs.
  const loadAuthConfig = async () => {
    const res = await AuthenticationService.authConfig().catch(() => null)
    if (!res) return
    const cfg = (res.data && res.data.data) || res.data || {}
    if (cfg.sso_login_url && !ssoLoginUrl.value) {
      ssoLoginUrl.value = cfg.sso_login_url
    }
  }

  /**
   * Resolve how this email should sign in.
   *
   * Returns true when SSO has taken over the screen (a prompt is now showing,
   * or the browser is being sent to Entra) and the caller should stop. Returns
   * false when no account was found for this email.
   */
  const resolveSsoForEmail = async (email) => {
    checkingSso.value = true
    const res = await AuthenticationService.checkSsoEnabled(email).catch(
      () => null
    )
    checkingSso.value = false

    const {
      sso_enabled,
      first_time_sso,
      never_signed_in,
      native_auth,
      sso_login_url
    } = (res && res.data && res.data.data) || {}

    if (!sso_enabled || !sso_login_url) return false

    ssoLoginUrl.value = sso_login_url
    nativeAuthEnabled.value = !!native_auth

    if (first_time_sso && never_signed_in && native_auth) {
      // Invited user creating a brand-new account: nothing to explain or
      // restore, so go straight to choosing a password.
      showSetPassword.value = true
    } else if (first_time_sso) {
      // Existing user whose sign-in method changed: explain the switch first.
      // Where the button leads depends on native auth - our own set-password
      // screen, or the hosted Entra "Forgot Password" page.
      showFirstTimePrompt.value = true
    } else {
      // Returning SSO user: straight to the hosted sign-in.
      window.location.href = withNext(sso_login_url)
    }

    return true
  }

  const continueToSso = () => {
    // Native auth: stay on this page and swap in the set-password fields, so
    // the code email is LimeIQ-branded and the user never leaves the app.
    // Mounting SsoSetPassword triggers the code email (its onMounted start).
    if (nativeAuthEnabled.value) {
      showFirstTimePrompt.value = false
      showSetPassword.value = true
      return
    }
    if (ssoLoginUrl.value) window.location.href = withNext(ssoLoginUrl.value)
  }

  return {
    checkingSso,
    showFirstTimePrompt,
    showSetPassword,
    nativeAuthEnabled,
    ssoLoginUrl,

    loadAuthConfig,
    resolveSsoForEmail,
    continueToSso
  }
}
